import moment from "moment-timezone";

/**
 * Filter dates to find extremes in the current week
 * @param {Date[]} dates - Array of dates to filter
 * @param {Object} appSettings - Application settings containing weekStartDay
 * @returns {{highestDate: Date|null, leastDate: Date|null}}
 */
export function filterAndFindExtremes(dates, appSettings) {
  try {
    if (!Array.isArray(dates)) {
      console.error("filterAndFindExtremes: Invalid dates array. Expected an array.");
      return { highestDate: null, leastDate: null };
    }

    if (dates.length === 0) {
      // Empty array is a valid initial state, not an error
      return { highestDate: null, leastDate: null };
    }

    const timeZone = global.config.getTimeZone();
    const dateObject = moment().tz(timeZone);
    const formattedDate = dateObject.format("YYYY-MM-DD HH:mm:ss Z");

    const currentDate = moment.tz(formattedDate, timeZone).toDate();

    let currentWeekStartDate = moment.tz(currentDate, timeZone).toDate();
    let currentWeekEndDate = moment.tz(currentWeekStartDate, timeZone).toDate();

    if (appSettings.weekStartDay && appSettings.weekStartDay === "Monday") {
      currentWeekStartDate.setDate(currentDate.getDate() - ((currentDate.getDay() + 6) % 7));
      currentWeekStartDate.setHours(0, 0, 0, 0);

      currentWeekEndDate = moment.tz(currentWeekStartDate, timeZone).toDate();
      const getDateNew = moment(currentWeekEndDate).add(6, "days");
      currentWeekEndDate.setDate(new Date(getDateNew.format("YYYY-MM-DD")).getDate());
      currentWeekEndDate.setHours(23, 59, 59, 999);
    } else {
      currentWeekStartDate.setDate(currentDate.getDate() - currentDate.getDay());
      currentWeekStartDate.setHours(0, 0, 0, 0);

      currentWeekEndDate = moment.tz(currentWeekStartDate, timeZone).toDate();
      const getDateNew = moment(currentWeekEndDate).add(6, "days");
      currentWeekEndDate.setDate(new Date(getDateNew.format("YYYY-MM-DD")).getDate());
      currentWeekEndDate.setHours(23, 59, 59, 999);
    }

    const datesInCurrentWeek = dates.filter((date) => {
      try {
        const currentDateObj = moment.tz(date, timeZone).toDate();
        return currentDateObj >= currentWeekStartDate && currentDateObj <= currentWeekEndDate;
      } catch (err) {
        console.error("filterAndFindExtremes: Error parsing date:", date, err);
        return false;
      }
    });

    if (datesInCurrentWeek.length === 0) {
      console.warn("filterAndFindExtremes: No dates found in current week.");
      return { highestDate: null, leastDate: null };
    }

    const highestDate = moment(Math.max(...datesInCurrentWeek.map(d => new Date(d).getTime()))).toDate();
    const leastDate = moment(Math.min(...datesInCurrentWeek.map(d => new Date(d).getTime()))).toDate();

    return { highestDate, leastDate };

  } catch (error) {
    console.error("filterAndFindExtremes: Unexpected error:", error);
    return { highestDate: null, leastDate: null };
  }
}

/**
 * Find extremes of dates in the last week
 * @param {Date[]} dates - Array of dates to filter
 * @param {Object} appSettings - Application settings containing weekStartDay
 * @returns {{highestLastWeekDate: Date|null, leastLastWeekDate: Date|null}}
 */
export function findExtremesOfLastWeek(dates, appSettings) {
  try {
    if (!Array.isArray(dates)) {
      console.error("findExtremesOfLastWeek: Invalid dates array. Expected an array.");
      return { highestLastWeekDate: null, leastLastWeekDate: null };
    }

    if (dates.length === 0) {
      // Empty array is a valid initial state, not an error
      return { highestLastWeekDate: null, leastLastWeekDate: null };
    }

    const currentDate = moment().tz(global.config.getTimeZone()).toDate();
    const dateObject = moment().tz(global.config.getTimeZone());
    const formattedDate = dateObject.format("YYYY-MM-DD");

    let lastWeekStartDate = moment.tz(formattedDate, global.config.getTimeZone()).toDate();

    if (appSettings.weekStartDay && appSettings.weekStartDay === "Monday") {
      // Monday to Sunday logic
      lastWeekStartDate.setDate(
        currentDate.getDate() - ((currentDate.getDay() + 6) % 7) - 7
      );
    } else {
      // Sunday to Saturday logic
      lastWeekStartDate.setDate(
        currentDate.getDate() - currentDate.getDay() - 7
      );
    }

    lastWeekStartDate.setHours(0, 0, 0, 0);

    let lastWeekEndDate = moment.tz(lastWeekStartDate, global.config.getTimeZone()).toDate();
    lastWeekEndDate.setDate(lastWeekStartDate.getDate() + 6);
    lastWeekEndDate.setHours(23, 59, 59, 999);

    // Filter dates that fall in the last week range
    const datesInLastWeek = dates.filter((date) => {
      try {
        const d = new Date(date);
        return d >= lastWeekStartDate && d <= lastWeekEndDate;
      } catch (err) {
        console.error("findExtremesOfLastWeek: Error parsing date:", date, err);
        return false;
      }
    });

    if (datesInLastWeek.length === 0) {
      console.warn("findExtremesOfLastWeek: No dates found in last week range.");
      return { highestLastWeekDate: null, leastLastWeekDate: null };
    }

    const highestLastWeekDate = moment.tz(
      Math.max(...datesInLastWeek.map(d => new Date(d).getTime())),
      global.config.getTimeZone()
    ).toDate();

    const leastLastWeekDate = moment.tz(
      Math.min(...datesInLastWeek.map(d => new Date(d).getTime())),
      global.config.getTimeZone()
    ).toDate();

    highestLastWeekDate.setHours(0, 0, 0, 0);
    leastLastWeekDate.setHours(0, 0, 0, 0);

    return { highestLastWeekDate, leastLastWeekDate };

  } catch (error) {
    console.error("findExtremesOfLastWeek: Unexpected error:", error);
    return { highestLastWeekDate: null, leastLastWeekDate: null };
  }
}

/**
 * Find extremes grouped by week
 * @param {Date[]} dates - Array of dates to process
 * @param {Object} appSettings - Application settings containing weekStartDay
 * @returns {Array<{highestWeekDate: Date, leastDate: Date}>}
 */
export function findExtremesByWeek(dates, appSettings) {
  try {
    if (!Array.isArray(dates)) {
      console.error("findExtremesByWeek: Invalid dates array. Expected an array.");
      return [];
    }

    if (dates.length === 0) {
      // Empty array is a valid initial state, not an error
      return [];
    }

    const datesByWeek = {};

    for (const date of dates) {
      try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
          console.warn("findExtremesByWeek: Skipping invalid date:", date);
          continue;
        }

        const weekStartDateFil = moment.tz(date, global.config.getTimeZone());
        const weekStartDateF = weekStartDateFil.format("YYYY-MM-DD");
        const weekStartDate = moment.tz(weekStartDateF, global.config.getTimeZone()).toDate();

        if (appSettings.weekStartDay && appSettings.weekStartDay === "Monday") {
          weekStartDate.setDate(dateObj.getDate() - ((dateObj.getDay() + 6) % 7));
        } else {
          weekStartDate.setDate(dateObj.getDate() - dateObj.getDay());
        }
        weekStartDate.setHours(0, 0, 0, 0);

        const weekStartDateString = weekStartDate.toISOString();

        if (!datesByWeek[weekStartDateString]) {
          datesByWeek[weekStartDateString] = [];
        }
        datesByWeek[weekStartDateString].push(dateObj);

      } catch (err) {
        console.error("findExtremesByWeek: Error processing date:", date, err);
      }
    }

    const extremesByWeek = [];

    for (const [week, weekDates] of Object.entries(datesByWeek)) {
      try {
        const maxDate = new Date(Math.max(...weekDates.map(d => d.getTime())));
        const minDate = new Date(Math.min(...weekDates.map(d => d.getTime())));

        const highestWeekDate = moment.tz(maxDate, global.config.getTimeZone()).toDate();
        const leastDate = moment.tz(minDate, global.config.getTimeZone()).toDate();

        extremesByWeek.push({ highestWeekDate, leastDate });

      } catch (err) {
        console.error("findExtremesByWeek: Error calculating extremes for week:", week, err);
      }
    }

    return extremesByWeek;

  } catch (error) {
    console.error("findExtremesByWeek: Unexpected error:", error);
    return [];
  }
}
